import React, { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import { Spin } from 'antd';
import { format } from 'date-fns';
import styled from 'styled-components';

import { getCommunityReviews } from 'apis/ReviewsAPI';
import { REVIEW_SOURCE } from 'components/GoogleMetricsReviews/reviews/constants';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import useDataQuarterSelection from 'hooks/useDataQuarterSelection';
import { getQuarterEndDate } from 'utils/dateQuarter';

import { REVIEWS_CSV_HEADERS } from './constants';
import Reviews from './Reviews';
import NoDataInfo from '../Widget/NoDataContent';
import { useWidgetContext } from '../Widget/WidgetContext';

const SpinWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
`;

export const ReviewsWidgetContent = () => {
  const { communityId } = useCommunityViewContext();

  const { filters, meta = { reviewsCommunityOverride: undefined }, setCSVData } = useWidgetContext();
  const { publication } = usePublicationContext();
  const { selection, isLatestQuarterSelected, snapshotUuids } = useDataQuarterSelection();
  const { reviewSources, reviewCategories } = filters;
  const { reviewsCommunityOverride } = meta;
  const reviewsCommunityId = reviewsCommunityOverride ?? communityId;
  const reviewsParams = {
    review_sources: reviewSources?.length > 0 ? reviewSources : null,
    review_categories: reviewCategories?.length > 0 ? reviewCategories : null,
    ...(!isLatestQuarterSelected ? { end_created_at: getQuarterEndDate(selection?.selectedQuarterSlug ?? null) } : {}),
    ...(publication?.hash ? { public_hash: publication.hash } : {}),
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery(
    [
      'reviews-widget',
      communityId,
      reviewsCommunityOverride,
      reviewSources,
      reviewCategories,
      isLatestQuarterSelected,
      selection?.selectedQuarterSlug,
    ],
    ({ pageParam = null }) =>
      getCommunityReviews(reviewsCommunityId, {
        nextPageUrl: pageParam,
        ...reviewsParams,
        page_size: PAGINATION_PAGE_SIZE.MODERATE,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: communityId > 0,
      getNextPageParam: (lastPage) => lastPage.next || undefined,
    }
  );

  const reviews = useMemo(() => data?.pages.flatMap((page) => page.results) ?? [], [data]);

  useEffect(() => {
    const communitiesData = reviews.map((item: any) => [
      item.name,
      item.text,
      item.rating,
      format(new Date(item.date), 'MMM dd, yyyy'),
      REVIEW_SOURCE[item.type],
      `[${item.categories.map((category: any) => category.label).join(',')}]`,
    ]);
    const csvData = [REVIEWS_CSV_HEADERS, ...communitiesData];
    setCSVData(csvData);
  }, [reviews, setCSVData]);

  const hasData = reviews.length > 0;

  return (
    <>
      {isLoading && (
        <SpinWrapper>
          <Spin />
        </SpinWrapper>
      )}
      {!isLoading && !hasData && <NoDataInfo>No Reviews Available</NoDataInfo>}
      {!isLoading && hasData && (
        <Reviews
          data={reviews}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
        />
      )}
    </>
  );
};

export default ReviewsWidgetContent;
